import { faMagnifyingGlassChart } from "@fortawesome/pro-regular-svg-icons";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { TableFooterExtraAction } from "@/components/TableFooter/table-footer.types";
import { useDeleteTrackerOrganizationsMutation } from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { useSignalScanRequests } from "../providers/SignalScanRequestsContext";

interface UseTrackerBulkActionsInput {
	selectedItems: number[];
	selectAll: boolean;
	/** Every company of the tracker, for Select all. */
	totalCount: number | undefined;
	signalsConfigured: boolean;
	clearSelection: () => void;
	notify: (message: string) => void;
}

/**
 * The Profile Tracker table's selection-bar actions: delete, and "Run signal search" (PREC-3240),
 * which works with Select all — every company of the tracker, not only the page in view. The search
 * itself, and its confirmation, belong to the surrounding `TrackerSignalSearchScope`. Extracted from
 * `TrackerDetailsTable` (over the 500-line ceiling).
 */
export const useTrackerBulkActions = ({
	selectedItems,
	selectAll,
	totalCount,
	signalsConfigured,
	clearSelection,
	notify,
}: UseTrackerBulkActionsInput) => {
	const { t } = useTranslation();
	const [showDelete, setShowDelete] = useState(false);
	const [deleteOrganizations, { isLoading: isDeleting }] = useDeleteTrackerOrganizationsMutation();
	const { requestSearch } = useSignalScanRequests();

	const handleDeleteOrganizations = async () => {
		try {
			await deleteOrganizations({ ids: selectedItems }).unwrap();
			clearSelection();
			setShowDelete(false);
			notify("Organizations deleted successfully");
		} catch (error) {
			notify("Failed to delete organizations");
		}
	};

	const hasSelection = selectedItems.length > 0;

	const deleteAction = hasSelection
		? {
				deleteOrganizations: () => setShowDelete(true),
				deleteOrganizationsIsLoading: isDeleting,
			}
		: undefined;

	const extraActions: TableFooterExtraAction[] =
		hasSelection && signalsConfigured
			? [
					{
						label: t("profileTracker.runSignalSearch.bulkAction"),
						icon: faMagnifyingGlassChart,
						onClick: () =>
							requestSearch(
								selectAll
									? { all: true, count: totalCount ?? selectedItems.length }
									: { ids: selectedItems }
							),
					},
				]
			: [];

	const modals = (
		<>
			{showDelete && (
				<Modal
					handleClose={() => setShowDelete(false)}
					handleSave={handleDeleteOrganizations}
					isLoading={isDeleting}
					isOpened={showDelete}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={t("basics.delete")}
					title="Delete Organizations"
				>
					<div className="text-sm text-gray-700 mb-4">
						Are you sure you want to delete {selectedItems.length} selected organization
						{selectedItems.length > 1 ? "s" : ""}? This action cannot be undone.
					</div>
				</Modal>
			)}
		</>
	);

	return { deleteAction, extraActions, modals };
};
